'use client';
/* eslint-disable tailwindcss/no-arbitrary-value -- the design fixes the arrow at 30px, and an empty label holds one line (1lh); no token carries either */

import classNames from 'classnames';
import { MaterialIcon } from '../../../components/MaterialIcon';
import type { ResolvedCompareSection } from '../types';
import {
  CompareRail,
  compareColumnClass,
  compareLabelClass,
  Pulse,
} from './CompareRail';
import { useScrollSync } from './ScrollSync';

export type SectionTableProps = {
  section: ResolvedCompareSection;
  /** Empty trailing columns keep the "+ Add Plan" column aligned while below the cap. */
  padColumns: number;
  /** Absent where the editor left the section without a heading. */
  heading?: string;
  /** A line under the heading. */
  note?: string;
  /** Rendered under the heading where the config marks the strongest value. */
  legend?: { highlight: string; rest: string };
  emptyValue: string;
  /** Set where the config makes sections collapsible; absent leaves the section always open. */
  collapse?: { expanded: boolean; onToggle: () => void; label: string };
  /** Labels of the per-section scroll arrows. */
  scrollLabels: { left: string; right: string };
  /** While the plans load: values, and labels not known yet, are pulsing bars of a line's height. */
  placeholder?: boolean;
};

/** A label band or a label cell: the design's attribute-type row. */
const labelText =
  'px-spacing/gap/soft-8px py-spacing/padding/y-sm text-font-size/table-cell-primary font-font-weight/table-cell-primary leading-line-height/table-cell-primary text-text/secondary-lighter';

/**
 * A value cell: fixed column, a rule to its left and below, the value centred. The rule is on the
 * left so there is one between the label column and the first value and none doubling the table's
 * own frame after the last; below 1280 the first cell sits on the frame itself and draws none.
 */
const cellFrame =
  'flex flex-col justify-center border-b border-l border-borders/default px-spacing/gap/soft-8px first:border-l-0 lg:first:border-l';
const cellText =
  'px-spacing/padding/x py-spacing/padding/y-sm text-center text-font-size/table-cell font-font-weight/table-cell-normal leading-line-height/table-cell';

/**
 * One section: heading, optional legend and note, then the rows in a rounded, ruled table. A missing
 * value renders as the configured dash, never as an empty cell; the winning cell gets the highlight
 * treatment.
 *
 * Each row is one flex line that wraps below 1280: the label takes the full width as a band and the
 * values drop into a scrolling rail beneath it. From 1280 nothing wraps and the label is the fixed
 * left column. One markup, so the two layouts cannot drift apart in content.
 */
export const SectionTable = ({
  section,
  padColumns,
  heading,
  note,
  legend,
  emptyValue,
  collapse,
  scrollLabels,
  placeholder = false,
}: SectionTableProps) => (
  <section
    data-component="compare-section"
    data-section={section.key}
    data-expanded={collapse ? collapse.expanded : undefined}
    className="pt-spacing/padding/section-y"
  >
    <div className="flex flex-col gap-spacing/gap/x-xs pb-spacing/gap/y">
      {collapse ? (
        // The toggle sits inside the heading, so the heading stays in the outline and the button
        // is named by its visible text; a section without a heading falls back to its key.
        <h2 className="mhg-alpha-typography-title-1 text-text/primary">
          <button
            type="button"
            onClick={collapse.onToggle}
            aria-expanded={collapse.expanded}
            aria-label={heading ? undefined : collapse.label}
            className="flex w-full items-center justify-between text-left"
          >
            {heading}
            <MaterialIcon
              icon={collapse.expanded ? 'expand_less' : 'expand_more'}
              px={24}
            />
          </button>
        </h2>
      ) : (
        heading && (
          <h2 className="mhg-alpha-typography-title-1 text-text/primary">
            {heading}
          </h2>
        )
      )}
      {legend && (
        <p
          data-component="compare-legend"
          className="text-font-size/table-cell font-font-weight/medium leading-line-height/table-cell text-[--text-secondary]"
        >
          <span className="font-bold text-text/accent">{legend.highlight}</span>
          {legend.rest}
        </p>
      )}
      {note && (
        <p
          data-component="compare-section-note"
          className="text-font-size/table-cell font-font-weight/medium leading-line-height/table-cell text-text/secondary-lighter"
        >
          {note}
        </p>
      )}
    </div>
    {(!collapse || collapse.expanded) && (
      // The table sits on a white band that runs the full width of the viewport on mobile, as the
      // design draws it; the page's own margin keeps the ruled table inset by it.
      <div className="-mx-spacing/margin/x bg-fill/card px-spacing/margin/x lg:mx-0 lg:px-0">
        <div className="relative">
          <div
            role="table"
            aria-label={heading}
            className="overflow-hidden rounded-radius/table border-x border-t border-borders/default"
          >
            {section.bandLabel && (
              <div className="flex flex-wrap border-b border-borders/default bg-fill/alternative-2 lg:flex-nowrap">
                <div className={compareLabelClass(labelText)}>
                  <span className="block px-spacing/gap/x-sm">
                    {section.bandLabel}
                  </span>
                </div>
              </div>
            )}
            {section.rows.map((row) => (
              <div
                key={row.key}
                role="row"
                data-component="compare-row"
                data-attribute={row.attributeKey || undefined}
                data-source={row.source}
                className="flex flex-wrap lg:flex-nowrap"
              >
                <div
                  role="rowheader"
                  className={compareLabelClass(
                    classNames(
                      labelText,
                      'border-b border-borders/default bg-fill/base',
                      // A continuation line of an offer row has no label of its own. Where labels
                      // are bands above the values it has no band; where they are a column the
                      // empty cell stays, so the values keep their columns.
                      !row.label && !placeholder && 'hidden lg:block',
                      // From 1280 the offer groups read as white rows with a deeper indent. The
                      // legacy `bg-fill/card` utility also sets a text colour, and its `lg:` copy
                      // outranks the base one, so the label's colour is restated at `lg:`.
                      row.source !== 'ATTRIBUTE' &&
                        'lg:bg-fill/card lg:px-spacing/padding/x lg:text-text/secondary-lighter',
                    ),
                  )}
                >
                  {/* An unlabelled cell still holds one line, so a one-line row keeps its height. */}
                  <span className="block min-h-[1lh] px-spacing/gap/x-sm">
                    {row.label ||
                      (placeholder && <Pulse className="w-32 max-w-full" />)}
                  </span>
                </div>
                <CompareRail>
                  {row.cells.map((cell, index) => {
                    const best = row.bestIndex === index;
                    return (
                      <div
                        // A cell is its plan's column: the position is the identity, and the page
                        // resets every rail when the set of plans changes.
                        // eslint-disable-next-line react/no-array-index-key
                        key={index}
                        role="cell"
                        data-best={best || undefined}
                        className={compareColumnClass(
                          classNames(
                            cellFrame,
                            best
                              ? 'bg-fill/primary-light text-text/accent'
                              : 'bg-fill/card text-text/primary',
                          ),
                        )}
                      >
                        <div className={cellText}>
                          {placeholder ? (
                            <Pulse className="mx-auto w-16 max-w-full" />
                          ) : (
                            (cell?.text ?? emptyValue)
                          )}
                        </div>
                      </div>
                    );
                  })}
                  {Array.from({ length: padColumns }, (_, index) => (
                    <div
                      key={`pad-${index}`}
                      role="cell"
                      className={compareColumnClass(
                        classNames(cellFrame, 'bg-fill/card'),
                      )}
                    />
                  ))}
                </CompareRail>
              </div>
            ))}
          </div>
          <SectionArrows labels={scrollLabels} />
        </div>
      </div>
    )}
  </section>
);

/**
 * The affordance for the offset every rail shares, on the edge of each section's table where there
 * is something to scroll to: → while columns remain to the right, ← while they remain to the left. A
 * tap moves exactly one column, so a value never comes to rest between two headers. Hidden from 768
 * up, where the columns fit and nothing scrolls.
 */
const SectionArrows = ({
  labels,
}: {
  labels: { left: string; right: string };
}) => {
  const sync = useScrollSync();
  if (!sync) return null;

  return (
    <>
      {(['left', 'right'] as const)
        .filter((side) => sync.arrows[side])
        .map((side) => (
          <button
            key={side}
            type="button"
            data-component="compare-scroll-arrow"
            data-side={side}
            aria-label={labels[side]}
            onClick={() => sync.scrollByColumn(side === 'left' ? -1 : 1)}
            className={classNames(
              'absolute top-1/2 flex size-[30px] -translate-y-1/2 items-center justify-center rounded-full bg-fill/card text-text/secondary-lighter shadow-md md:hidden',
              side === 'left'
                ? 'left-0 -translate-x-1/2'
                : 'right-0 translate-x-1/2',
            )}
          >
            <MaterialIcon
              icon={side === 'left' ? 'arrow_back' : 'arrow_forward'}
              px={20}
            />
          </button>
        ))}
    </>
  );
};
